/**
 * Next.js App Router–compatible navigation hooks backed by react-router-dom.
 * Keeps existing router.push / useSearchParams().get call sites working.
 */
import { useMemo } from 'react'
import {
  useNavigate,
  useLocation,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from 'react-router-dom'

export function useRouter() {
  const navigate = useNavigate()
  // Stable identity — callers often put `router` in useEffect deps; a new
  // object every render re-fired student redirects on every mic setState.
  return useMemo(
    () => ({
      push: (to: string) => {
        navigate(to)
      },
      replace: (to: string) => {
        navigate(to, { replace: true })
      },
      back: () => {
        navigate(-1)
      },
      prefetch: async (_to: string) => {
        // No-op under Vite SPA; optional prefetch was never required for correctness
      },
    }),
    [navigate]
  )
}

export function usePathname(): string {
  return useLocation().pathname
}

/** Matches Next's useSearchParams(): returns URLSearchParams (not a tuple). */
export function useSearchParams(): URLSearchParams {
  const [params] = useRRSearchParams()
  return params
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return useRRParams() as T
}
